/**
 * Mobile Modal for Product Card Variants
 * Opens a bottom sheet modal on mobile devices for variant selection
 */

class CardMobileModal {
  constructor() {
    this.modal = document.getElementById('card-mobile-modal');
    if (!this.modal) return;
    
    this.overlay = this.modal.querySelector('.card-mobile-modal__overlay');
    this.content = this.modal.querySelector('.card-mobile-modal__content');
    this.header = this.modal.querySelector('.card-mobile-modal__header');
    this.title = this.modal.querySelector('.card-mobile-modal__title');
    this.closeBtn = this.modal.querySelector('.card-mobile-modal__close');
    this.body = this.modal.querySelector('.card-mobile-modal__body');
    
    this.currentProductId = null;
    this.touchStartY = 0;
    this.touchEndY = 0;
    
    this.init();
  }
  
  init() {
    // Bind click events to all mobile add buttons
    this.bindMobileAddButtons();
    
    // Bind close events
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }
    
    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }
    
    // Swipe down to close
    if (this.content) {
      this.content.addEventListener('touchstart', (e) => {
        this.touchStartY = e.touches[0].clientY;
      });
      
      this.content.addEventListener('touchmove', (e) => {
        this.touchEndY = e.touches[0].clientY;
      });
      
      this.content.addEventListener('touchend', () => {
        const swipeDistance = this.touchEndY - this.touchStartY;
        // If swiped down more than 100px, close modal
        if (swipeDistance > 100 && this.body.scrollTop === 0) {
          this.close();
        }
      });
    }
    
    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('active')) {
        this.close();
      }
    });
    
    // Listen for cart updates to close modal
    document.addEventListener('cartUpdate', () => {
      this.close();
    });
  }
  
  bindMobileAddButtons() {
    // Use event delegation for dynamically added buttons
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.card__mobile-add-btn');
      if (btn) {
        e.preventDefault();
        e.stopPropagation();
        const productId = btn.getAttribute('data-product-id');
        this.open(productId, btn);
      }
    });
  }
  
  open(productId, triggerButton) {
    this.currentProductId = productId;
    
    // Find the product card
    const card = triggerButton.closest('.card-wrapper');
    if (!card) return;
    
    // Get the hover overlay content (which has the form)
    const hoverOverlay = card.querySelector('.card__hover-overlay');
    if (!hoverOverlay) return;
    
    // Clone the hover content
    const hoverContent = hoverOverlay.querySelector('.card__hover-content');
    if (!hoverContent) return;
    
    const clonedContent = hoverContent.cloneNode(true);
    
    // Update IDs to avoid conflicts
    this.updateFormIds(clonedContent, productId);
    
    // Set title
    const titleElement = clonedContent.querySelector('.card__hover-title');
    if (titleElement) {
      this.title.textContent = titleElement.textContent;
      titleElement.remove(); // Remove from body since it's in header
    }
    
    // Insert into modal body
    this.body.innerHTML = '';
    this.body.appendChild(clonedContent);
    
    // Store current scroll position
    this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
    
    // Show modal
    this.modal.classList.add('active');
    document.body.classList.add('card-mobile-modal-open');
    document.body.style.top = `-${this.scrollPosition}px`;
    
    // Set up variant selection for the cloned form
    this.setupVariantSelection();
  }
  
  close() {
    this.modal.classList.remove('active');
    document.body.classList.remove('card-mobile-modal-open');
    
    // Restore scroll position
    document.body.style.top = '';
    if (this.scrollPosition !== undefined) {
      window.scrollTo(0, this.scrollPosition);
    }
    
    // Clear content after animation
    setTimeout(() => {
      this.body.innerHTML = '';
      this.currentProductId = null;
    }, 300);
  }
  
  updateFormIds(content, productId) {
    // Update all IDs and for attributes to be unique for modal
    const inputs = content.querySelectorAll('input[type="radio"]');
    inputs.forEach((input, index) => {
      const oldId = input.id;
      const newId = `modal-${oldId}`;
      input.id = newId;
      
      const label = content.querySelector(`label[for="${oldId}"]`);
      if (label) {
        label.setAttribute('for', newId);
      }
    });
  }
  
  setupVariantSelection() {
    const form = this.body.querySelector('form');
    if (!form) return;
    
    const variantInput = form.querySelector('input[name="id"]');
    const variantSelect = form.querySelector('[data-variant-select]');
    const priceDisplay = form.querySelector('.btn-price');
    const addButton = form.querySelector('.card__hover-add-btn');
    
    if (!variantInput || !variantSelect || !priceDisplay) return;
    
    // Get all option inputs
    const optionInputs = form.querySelectorAll('.card__hover-option-input');
    
    // Update variant when options change
    optionInputs.forEach(input => {
      input.addEventListener('change', () => {
        this.updateSelectedVariant(form, variantInput, variantSelect, priceDisplay, addButton);
      });
    });
    
    // Initialize variant on modal open (set default selected variant)
    this.updateSelectedVariant(form, variantInput, variantSelect, priceDisplay, addButton);
  }
  
  updateSelectedVariant(form, variantInput, variantSelect, priceDisplay, addButton) {
    // Get selected options
    const selectedOptions = {};
    const optionGroups = form.querySelectorAll('.card__hover-option-group');
    
    optionGroups.forEach(group => {
      const checkedInput = group.querySelector('.card__hover-option-input:checked');
      if (checkedInput) {
        const optionName = checkedInput.name.match(/options\[(.*?)\]/)[1];
        selectedOptions[optionName] = checkedInput.value;
      }
    });
    
    // Find matching variant
    const options = Array.from(variantSelect.options);
    let matchedVariant = null;
    
    for (const option of options) {
      const variantTitle = option.textContent.split(' - ')[0].trim();
      const optionValues = Object.values(selectedOptions);
      
      // Check if all selected options are in this variant
      const allMatch = optionValues.every(value => 
        variantTitle.includes(value.trim())
      );
      
      if (allMatch) {
        matchedVariant = option;
        break;
      }
    }
    
    // Update hidden input, select and price
    if (matchedVariant) {
      variantInput.value = matchedVariant.value;
      variantSelect.value = matchedVariant.value;
      const price = matchedVariant.getAttribute('data-price-formatted');
      if (price && priceDisplay) {
        priceDisplay.textContent = price;
      }
      
      // Enable/disable add button based on availability
      if (addButton) {
        if (matchedVariant.disabled) {
          addButton.disabled = true;
          addButton.querySelector('.btn-text').textContent = 'Sold Out';
        } else {
          addButton.disabled = false;
          addButton.querySelector('.btn-text').textContent = 'Add to Cart';
        }
      }
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CardMobileModal();
  });
} else {
  new CardMobileModal();
}

